/**
 * Bill Runs: why bills mismatch. Three views over the bills' reports
 * (billReport, so the same compared sections the client report prints):
 *
 *   Sections   per section, how many bills differ, over and under
 *   Retailers  per retailer, bills validated, how many mismatched, over and under
 *
 * Counts only, never summed $: a total hides the bills behind it. The amount
 * shown is a recurring difference's per-bill amount.
 *   Recurring  the same difference (section + amount + retailer) on many bills —
 *              one cause, usually a rate in our own setup, not one error per bill.
 *              The June 2025 MultiSite file had 166 bills at exactly −$0.55 metering.
 *
 * A section difference under NOISE is rounding, not a driver (classifyVariance
 * treats ≤ $0.50 on the whole bill as perfect for the same reason).
 */
import type { BvBill, ShadowBillSection } from '../types/billTypes';
import type { BillReport } from './billVerdict';
import type { BillRunRow } from './billRunRows';
import { billRunStatus } from './billRunStatus';
import { siteValue } from './siteFilters';

export type DriverBill = { billId: string; retailer: string; report: BillReport };

export type SectionDriver = {
  section: ShadowBillSection;
  label: string;
  /** Bills where this section differs by more than NOISE. */
  bills: number;
  /** Bills where the retailer charged more in this section. */
  over: number;
  /** Bills where the retailer charged less in this section. */
  under: number;
};

export type RetailerDriver = {
  retailer: string;
  /** Bills with a report. */
  bills: number;
  /** Overcharge or undercharge. */
  mismatched: number;
  /** Mismatched ÷ bills, 0–100, one decimal. */
  rate: number;
  /** Overcharged bills. */
  over: number;
  /** Undercharged bills. */
  under: number;
};

export type RecurringDriver = {
  key: string;
  section: ShadowBillSection;
  label: string;
  amount: number;
  retailer: string;
  billIds: string[];
};

export type MismatchDrivers = {
  sections: SectionDriver[];
  retailers: RetailerDriver[];
  recurring: RecurringDriver[];
};

const NOISE = 0.5;
const RECURRING_MIN_BILLS = 2;
const RECURRING_TOP = 5;
const round2 = (n: number) => Math.round(n * 100) / 100;

export const driverKey = (section: string, amount: number, retailer: string) =>
  `${section}|${amount.toFixed(2)}|${retailer}`;

export function mismatchDrivers(bills: readonly DriverBill[]): MismatchDrivers {
  const sections = new Map<ShadowBillSection, SectionDriver>();
  const retailers = new Map<string, RetailerDriver>();
  const recurring = new Map<string, RecurringDriver>();

  for (const { billId, retailer, report } of bills) {
    const r = retailers.get(retailer) ?? { retailer, bills: 0, mismatched: 0, rate: 0, over: 0, under: 0 };
    retailers.set(retailer, r);
    r.bills += 1;
    const v = report.verdict;
    if (v.bucket !== 'within_range') r.mismatched += 1;
    if (v.bucket === 'overcharge') r.over += 1;
    if (v.bucket === 'undercharge') r.under += 1;

    for (const s of report.sections) {
      if (s.key === 'not_compared') continue;
      const diff = round2(s.actual - s.shadow);
      if (Math.abs(diff) <= NOISE) continue;
      const sd = sections.get(s.key) ?? { section: s.key, label: s.label, bills: 0, over: 0, under: 0 };
      sections.set(s.key, sd);
      sd.bills += 1;
      if (diff > 0) sd.over += 1; else sd.under += 1;

      const key = driverKey(s.key, diff, retailer);
      const rd = recurring.get(key) ?? { key, section: s.key, label: s.label, amount: diff, retailer, billIds: [] };
      recurring.set(key, rd);
      rd.billIds.push(billId);
    }
  }

  return {
    sections: [...sections.values()]
      .sort((a, b) => b.bills - a.bills),
    retailers: [...retailers.values()]
      .map((r) => ({
        ...r,
        rate: r.bills ? Math.round((r.mismatched / r.bills) * 1000) / 10 : 0,
      }))
      .sort((a, b) => b.mismatched - a.mismatched || b.bills - a.bills),
    recurring: [...recurring.values()]
      .filter((r) => r.billIds.length >= RECURRING_MIN_BILLS)
      .sort((a, b) => b.billIds.length - a.billIds.length || Math.abs(b.amount) - Math.abs(a.amount))
      .slice(0, RECURRING_TOP),
  };
}

/**
 * The drivers over Bill Runs rows: each bill once (a bill spanning two months
 * is two rows), archived bills and bills without a report left out. `keep`
 * narrows the rows first (Bill Runs passes its conditions, minus the driver).
 */
export function driversFromRows(
  rows: readonly BillRunRow[],
  reportFor: (bill: BvBill) => BillReport | null,
  keep: (row: BillRunRow) => boolean = () => true,
): MismatchDrivers {
  const seen = new Set<string>();
  const out: DriverBill[] = [];
  for (const r of rows) {
    if (r.kind !== 'bill' || r.archived || !r.inScope || !r.figures || seen.has(r.board.bill.id) || !keep(r)) continue;
    seen.add(r.board.bill.id);
    const report = reportFor(r.board.bill);
    if (report) out.push({ billId: r.board.bill.id, retailer: siteValue(r.board.bill, 'retailer'), report });
  }
  return mismatchDrivers(out);
}

/**
 * Each client's most common reason for a mismatch, for the Dashboard's
 * Over/under by client table: per over/undercharged bill, the compared section
 * with the largest difference ("Network charges higher"), then the reason the
 * most of that client's bills share, with its count. Same reports as the
 * drivers above, so no extra reads.
 */
export function clientMainReasons(
  rows: readonly BillRunRow[],
  reportFor: (bill: BvBill) => BillReport | null,
): Map<string, { reason: string; bills: number }> {
  const seen = new Set<string>();
  const counts = new Map<string, Map<string, number>>();
  for (const r of rows) {
    if (r.kind !== 'bill' || r.archived || !r.inScope || seen.has(r.board.bill.id)) continue;
    seen.add(r.board.bill.id);
    const status = billRunStatus(r);
    if (status.details !== 'overcharge' && status.details !== 'undercharge') continue;
    const report = reportFor(r.board.bill);
    if (!report) continue;
    let top: { label: string; diff: number } | null = null;
    for (const s of report.sections) {
      if (s.key === 'not_compared') continue;
      const diff = round2(s.actual - s.shadow);
      if (Math.abs(diff) > NOISE && (!top || Math.abs(diff) > Math.abs(top.diff))) top = { label: s.label, diff };
    }
    const reason = top ? `${top.label} ${top.diff > 0 ? 'higher' : 'lower'}` : 'Total differs';
    const entity = siteValue(r.board.bill, 'entity');
    const c = counts.get(entity) ?? new Map<string, number>();
    counts.set(entity, c);
    c.set(reason, (c.get(reason) ?? 0) + 1);
  }
  const out = new Map<string, { reason: string; bills: number }>();
  for (const [entity, c] of counts) {
    const [reason, bills] = [...c].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];
    out.set(entity, { reason, bills });
  }
  return out;
}
